import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Tooltip } from "@mui/material";
import { format } from "date-fns";
import { Ban, Download, FileText, Pencil, X } from "lucide-react";

import { Button } from "components/ui/Button";
import { Pill } from "components/ui/Pill";
import { CancelReviewModal } from "features/cdm/components/CancelReviewModal/CancelReviewModal";
import { CarePlanForm } from "features/cdm/components/CarePlanForm/CarePlanForm";
import { CdmReviewReferences } from "features/cdm/components/CdmReviewReferences";
import { LoadingSection } from "features/cdm/components/LoadingSection/LoadingSection";
import { ReviewAndExamForm } from "features/cdm/components/ReviewAndExamForm/ReviewAndExamForm";
import { PCRS_CONNECTION_REQUIRED_MESSAGE } from "features/cdm/constants/pcrsSchemeCheck";
import {
  getReviewTypeTitle,
  isRegistration,
  REVIEW_TYPES,
} from "features/cdm/constants/reviewTypes";
import { useCancelReview } from "features/cdm/hooks/useCancelReview";
import { useDownloadCdmClinicalExportPdf } from "features/cdm/hooks/useDownloadCdmClinicalExportPdf";
import { useGetCarePlan } from "features/cdm/hooks/useGetCarePlan";
import { views } from "features/cdm/hooks/useView";
import { extractSubmissionError } from "features/cdm/utils/extractSubmissionError";
import {
  isOwnReview,
  NOT_OWNER_MESSAGE,
} from "features/cdm/utils/reviewOwnership";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { formatCdmStatus, getCdmStatusColors } from "utils/cdmStatus";

import { CdmMessagePreview } from "../../../components/CdmMessagePreview/CdmMessagePreview";
import { formatCorrectedDate } from "../../../utils/reviewDate";
import { useGetPP } from "../../hooks/useGetPP";
import { PlanAndOutcomeForm } from "../PPItemForm/components/PlanAndOutcomeForm/PlanAndOutcomeForm";
import { RiskAndAssessmentForm } from "../PPItemForm/components/RiskAndAssessmentForm/RiskAndAssessmentForm";
import { TestAndMedicationForm } from "../PPItemForm/components/TestAndMedicationForm/TestAndMedicationForm";
import { useSendPP } from "../PPItemForm/hooks/useSendPP";
import { useValidatePP } from "../PPItemForm/hooks/useValidatePP";
import { PpEditSteps } from "./PpEditSteps";
import { PpInitialStep } from "./PpInitialStep";
import style from "./PPPreview.module.css";
import { PpPreviewContent } from "./PpPreviewContent";

export const PPPreview = ({
  item,
  setView,
  patientId,
  startEditing,
  onOpenCarePlan,
  isMostRecent = false,
  actionsDisabled = false,
}) => {
  const { data: patient } = usePatientData();
  const missingPatientFields = [
    !patient?.ethnicityCode && "Ethnic Group",
    !patient?.address?.country && "Country",
    !patient?.financialClass && "Patient Classification",
  ].filter(Boolean);

  const [isEditing, setIsEditing] = useState(false);
  const [editActiveStep, setEditActiveStep] = useState(1);
  const [editData, setEditData] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const cancelPp = useCancelReview({ patientId, programme: "PP" });
  const { mutate: downloadClinicalExport, isPending: isDownloadingClinicalExport } =
    useDownloadCdmClinicalExportPdf();

  useEffect(() => {
    if (!item) {
      setView({ name: views.noChronicDiseases, cdmId: null });
    }
  }, [item, setView]);

  const { data, isLoading } = useGetPP(item?.id);

  const { data: currentStaff } = useStaffInfo();
  const isOwn = isOwnReview({
    doctorId: data?.doctorId,
    doctorName: data?.reviewingDoctor,
    staffId: currentStaff?.staffMember?.id,
    staffName: currentStaff?.staffMember
      ? `${currentStaff.staffMember.firstName} ${currentStaff.staffMember.lastName}`
      : null,
  });

  // Cancel is limited to a programme's registration review with no subsequent
  // reviews (isMostRecent). Annual/subsequent reviews can never be cancelled.
  const cancelDisabledReason = !isRegistration(item?.reviewType)
    ? "Only a programme's registration review can be cancelled."
    : !isOwn
    ? NOT_OWNER_MESSAGE
    : !isMostRecent
    ? "This registration cannot be cancelled — the programme has subsequent reviews."
    : data?.clinicalMessageStatus !== "ACKNOWLEDGED" ||
      data?.reimbursementMessageStatus !== "ACKNOWLEDGED"
    ? "You can cancel only once both messages are acknowledged."
    : !data?.reimbursementMessageControlId
    ? "This review cannot be cancelled — no reimbursement message."
    : null;

  const handleConfirmCancel = () => {
    if (!data?.reimbursementMessageControlId) {
      setShowCancelConfirm(false);

      return;
    }

    cancelPp.mutate(
      {
        originalReimbursementMessageControlId:
          data.reimbursementMessageControlId,
      },
      {
        onSettled: () => {
          setShowCancelConfirm(false);
          setView({ name: views.noChronicDiseases, cdmId: null });
        },
      }
    );
  };

  useEffect(() => {
    if (startEditing && data && item && !isEditing && !actionsDisabled) {
      setEditData({ ...data, reviewType: item.reviewType });
      setEditActiveStep(1);
      setIsEditing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startEditing, !!data, item?.id]);

  const { data: carePlanView } = useGetCarePlan(item?.id);
  const { mutateAsync: validatePP } = useValidatePP({ patientId });
  const { mutateAsync: sendPP, isPending: isSendingPP } = useSendPP({
    patientId,
  });

  if (!item) {
    return null;
  }

  if (!data || isLoading) {
    return <LoadingSection />;
  }

  const editIsAnnualReview = item?.reviewType === REVIEW_TYPES.PP_ANNUAL;

  const handleStartEdit = () => {
    if (actionsDisabled) return;

    setEditData({ ...data, reviewType: item.reviewType });
    setEditActiveStep(1);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData(null);
    setEditActiveStep(1);
  };

  return (
    <section className={style.wrapper}>
      <div className={style.header}>
        <div className={style.headerInner}>
          <h3>{item.name || getReviewTypeTitle(data.reviewType)}</h3>
          <Pill
            color={getCdmStatusColors(data.status).color}
            bg={getCdmStatusColors(data.status).bg}
            border={getCdmStatusColors(data.status).border}
          >
            {formatCdmStatus(data.status)}
          </Pill>
          <p>{format(new Date(item.date || item.reviewDate), "dd-MM-yyyy")}</p>
          {!!formatCorrectedDate(data.correctedDate ?? item.correctedDate) && (
            <p className={style.correctedOn}>
              Corrected on{" "}
              {formatCorrectedDate(data.correctedDate ?? item.correctedDate)}
            </p>
          )}
        </div>
        <div className={style.statusWrapper}>
          {!isEditing && (
            <>
              <Tooltip
                title={
                  actionsDisabled
                    ? PCRS_CONNECTION_REQUIRED_MESSAGE
                    : isOwn
                    ? "Edit review"
                    : NOT_OWNER_MESSAGE
                }
              >
                <span>
                  <Button
                    variant="outlineGray"
                    iconButton
                    disabled={!isOwn || actionsDisabled}
                    onClick={handleStartEdit}
                    aria-label="Edit review"
                  >
                    <Pencil size={16} />
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  actionsDisabled
                    ? PCRS_CONNECTION_REQUIRED_MESSAGE
                    : cancelDisabledReason || "Cancel review"
                }
              >
                <span>
                  <Button
                    variant="outlineGray"
                    iconButton
                    disabled={
                      !!cancelDisabledReason ||
                      cancelPp.isPending ||
                      actionsDisabled
                    }
                    onClick={() => setShowCancelConfirm(true)}
                    aria-label="Cancel review"
                  >
                    <Ban size={16} />
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Care plan">
                <span>
                  <Button
                    variant="outlineGray"
                    iconButton
                    onClick={() => onOpenCarePlan?.(item?.id)}
                    aria-label="Care plan"
                  >
                    <FileText size={16} />
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          <Tooltip title="Download clinical data (PDF)">
            <span>
              <Button
                variant="outlineGray"
                iconButton
                disabled={isDownloadingClinicalExport}
                onClick={() =>
                  downloadClinicalExport({
                    reviewId: item?.id,
                    tenantVal: localStorage.getItem("kinesin-tenant"),
                  })
                }
                aria-label="Download clinical data (PDF)"
              >
                <Download size={16} />
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlineGray"
            iconButton
            onClick={() =>
              setView({ name: views.noChronicDiseases, cdmId: null })
            }
            aria-label="Close preview"
          >
            <X size={16} />
          </Button>
        </div>
      </div>
      {(data.statusErrors?.length > 0 || data.statusWarnings?.length > 0) &&
        !isEditing && (
          <div className={style.statusMessages}>
            {data.statusErrors?.map((msg, i) => (
              <div key={i} className={style.statusBox}>
                <p className={style.statusBoxTitleError}>Rejection</p>
                <p className={style.statusBoxItemError}>{msg}</p>
              </div>
            ))}
            {data.statusWarnings?.map((msg, i) => (
              <div
                key={i}
                className={`${style.statusBox} ${style.statusBoxWarning}`}
              >
                <p className={style.statusBoxTitleWarning}>Warning</p>
                <p className={style.statusBoxItemWarning}>{msg}</p>
              </div>
            ))}
          </div>
        )}
      {isEditing ? (
        <>
          {missingPatientFields.length > 0 && (
            <div
              style={{
                alignSelf: "stretch",
                backgroundColor: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: "12px",
                padding: "16px",
                margin: "0 24px 16px",
                color: "#856404",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              ⚠️ PP cannot be submitted — patient is missing{" "}
              {missingPatientFields.join(" and ")}. Please update the patient
              profile first.
            </div>
          )}
          <PpEditSteps activeStep={editActiveStep} />
          <CdmReviewReferences patientId={patientId} />
          {editActiveStep === 1 && (
            <PpInitialStep
              data={editData}
              onContinue={(stepData) => {
                setEditData((prev) => ({ ...prev, ...stepData }));
                setEditActiveStep(2);
              }}
              onCancel={handleCancelEdit}
              submitDisabled={missingPatientFields.length > 0}
            />
          )}
          {editActiveStep === 2 && (
            <ReviewAndExamForm
              data={editData}
              isPP={true}
              onContinue={(newData) => {
                setEditData((prev) => ({ ...prev, ...newData }));
                setEditActiveStep(3);
              }}
              onBack={() => setEditActiveStep(1)}
            />
          )}
          {editActiveStep === 3 && (
            <TestAndMedicationForm
              data={editData}
              medications={item.medications ?? []}
              isAnnualReview={editIsAnnualReview}
              isRegistration={isRegistration(item?.reviewType)}
              onBack={() => setEditActiveStep(2)}
              onContinue={async (newData) => {
                const newDataToSend = { ...editData, ...newData };
                const res = await validatePP(newDataToSend);

                if (res.isValid) {
                  setEditData(newDataToSend);
                  setEditActiveStep(4);
                } else {
                  toast.error(
                    res.validationErrors?.length > 0
                      ? res.validationErrors.join(", ")
                      : "Something went wrong"
                  );
                }
              }}
            />
          )}
          {editActiveStep === 4 && (
            <RiskAndAssessmentForm
              data={editData}
              isAnnualReview={editIsAnnualReview}
              onBack={() => setEditActiveStep(3)}
              onContinue={(newData) => {
                setEditData((prev) => ({ ...prev, ...newData }));
                setEditActiveStep(5);
              }}
            />
          )}
          {editActiveStep === 5 && (
            <PlanAndOutcomeForm
              data={editData}
              onBack={() => setEditActiveStep(4)}
              onContinue={(newData) => {
                setEditData((prev) => ({ ...prev, ...newData }));
                setEditActiveStep(6);
              }}
            />
          )}
          {editActiveStep === 6 && (
            <CarePlanForm
              data={editData}
              carePlanView={carePlanView}
              isPp
              isRegistration={isRegistration(item?.reviewType)}
              isLoading={isSendingPP}
              onBack={() => setEditActiveStep(5)}
              onContinue={async (newData) => {
                const dataToSend = { ...editData, ...newData };

                try {
                  const res = await sendPP(dataToSend);

                  if (res?.success !== false) {
                    toast.success("PP updated successfully");
                    handleCancelEdit();
                    setView({ name: views.noChronicDiseases, cdmId: null });
                  } else {
                    toast.error(
                      extractSubmissionError(res, "Error while updating PP")
                    );
                  }
                } catch (error) {
                  toast.error(
                    extractSubmissionError(error, "Error while updating PP")
                  );
                }
              }}
            />
          )}
        </>
      ) : (
        <>
          <CdmMessagePreview xml={data.clinicalMessageXml} />
          <PpPreviewContent data={data} />
        </>
      )}
      <CancelReviewModal
        isOpen={showCancelConfirm}
        isCancelling={cancelPp.isPending}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleConfirmCancel}
      />
    </section>
  );
};
